import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Layout,
  Card,
  Button, 
  InlineStack, 
  BlockStack,
  Badge,
  Modal,
  Form,
  FormLayout,
  TextField,
  Banner,
  Spinner,
  Pagination,
  Text,
  Checkbox,
  DataTable,
  Select
} from '@shopify/polaris';
import { productsAPI, addOnsAPI } from '../services/api';
import shopifyApi from '../services/shopifyApi';
import RentalModal from '../components/RentalModal';

const RENTAL_PAGE_SIZE = 25;
const SHOPIFY_PAGE_SIZE = 50;
const JACKET_CATEGORY_IDS = ['categoryID_148', 'categoryID_141', 'categoryID_135'];
const PANTS_CATEGORY_IDS = ['categoryID_142', 'categoryID_136', 'categoryID_151'];

const collectShopifyIdsFromProducts = (productList) => {
  const ids = new Set();
  (productList || []).forEach((product) => {
    if (product.shopifyProductId) ids.add(product.shopifyProductId);
    if (product.jacketProductId) ids.add(product.jacketProductId);
    if (product.pantsProductId) ids.add(product.pantsProductId);
  });
  return Array.from(ids);
};

const filterByCategoryTags = (products, categoryIds) =>
  (products || []).filter(
    (product) => product.tags && product.tags.some((tag) => categoryIds.includes(tag))
  );

const Products = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalActive, setModalActive] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [bundleFilter, setBundleFilter] = useState('bundles'); // Default to bundles only
  const [sortBy, setSortBy] = useState('category');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Rental booking state
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [rentalModalActive, setRentalModalActive] = useState(false);
  
  // Shopify products state (lookup map avoids repeated array scans)
  const [shopifyProductLookup, setShopifyProductLookup] = useState({});
  const [modalShopifyProducts, setModalShopifyProducts] = useState([]);
  const [selectedShopifyProducts, setSelectedShopifyProducts] = useState([]);
  const [selectedVariants, setSelectedVariants] = useState({});
  const [loadingShopify, setLoadingShopify] = useState(false);
  const [shopifySearchQuery, setShopifySearchQuery] = useState('');
  const [shopifyModalCursor, setShopifyModalCursor] = useState(null);
  const [shopifyModalHasMore, setShopifyModalHasMore] = useState(false);
  const shopifySearchTimeoutRef = useRef(null);
  const addOnsLoadedRef = useRef(false);
  
  // Add-ons state
  const [availableAddOns, setAvailableAddOns] = useState([]);
  const [selectedAddOns, setSelectedAddOns] = useState([]);
  const [addOnSettings, setAddOnSettings] = useState({});

  // Bundle product selection state
  const [jacketProducts, setJacketProducts] = useState([]);
  const [pantsProducts, setPantsProducts] = useState([]);
  const [selectedJacket, setSelectedJacket] = useState(null);
  const [selectedPants, setSelectedPants] = useState(null);
  const [jacketSearchQuery, setJacketSearchQuery] = useState('');
  const [pantsSearchQuery, setPantsSearchQuery] = useState('');
  
  // Bulk actions state
  const [selectedProductIds, setSelectedProductIds] = useState([]);

  // Per-unit condition tracking
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [conditionProduct, setConditionProduct] = useState(null);
  const [conditionRecords, setConditionRecords] = useState([]);
  const [conditionsLoading, setConditionsLoading] = useState(false);
  const [conditionsSaving, setConditionsSaving] = useState(false);
  const [conditionError, setConditionError] = useState('');
  
  // Form state
  const [formData, setFormData] = useState({
    isBundle: false,
    shopifyProductId: '',
    shopifyVariantId: '',
    title: '',
    priceStage1: { enabled: true, price: '33', minDays: 0 },
    priceStage2: { enabled: true, price: '28', minDays: 5 },
    priceStage3: { enabled: true, price: '22', minDays: 7 },
    priceStage4: { enabled: false, price: '', minDays: 14 },
    securityDeposit: '',
    minimumRentalDays: '3',
    bufferBefore: '3',
    bufferAfter: '4',
    totalQuantity: '',
    categories: '',
    tags: '',
  });

  const mergeShopifyProducts = useCallback((products) => {
    if (!products?.length) return;
    setShopifyProductLookup((prev) => {
      const next = { ...prev };
      products.forEach((product) => {
        next[product.id.toString()] = product;
      });
      return next;
    });
  }, []);

  const shopifyProducts = useMemo(
    () => (modalActive ? modalShopifyProducts : Object.values(shopifyProductLookup)),
    [modalActive, modalShopifyProducts, shopifyProductLookup]
  );

  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      
      // Build query parameters based on bundle filter
      const params = {
        page: currentPage,
        limit: RENTAL_PAGE_SIZE,
        sortBy,
        sortDirection,
      };

      if (debouncedSearchQuery) {
        params.search = debouncedSearchQuery;
      }

      if (categoryFilter) {
        params.category = categoryFilter;
      }

      if (statusFilter === 'active') {
        params.isActive = 'true';
      } else if (statusFilter === 'inactive') {
        params.isActive = 'false';
      }
      
      if (bundleFilter === 'bundles') {
        params.isBundle = 'true';
      } else if (bundleFilter === 'non-bundles') {
        params.isBundle = 'false';
      } else {
        params.isBundle = 'all';
      }
      
      const response = await productsAPI.getAll(params);
      setProducts(response.data?.products || []);
      setTotalPages(response.data?.totalPages || 1);
      setTotalProducts(response.data?.totalProducts || 0);
    } catch (err) {
      setError('Failed to load products');
      setProducts([]);
      setTotalPages(1);
      console.error('Products error:', err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, bundleFilter, categoryFilter, debouncedSearchQuery, statusFilter, sortBy, sortDirection]);

  const fetchAddOns = useCallback(async () => {
    if (addOnsLoadedRef.current) return;
    try {
      const response = await addOnsAPI.getAll({ limit: 500 });
      setAvailableAddOns(response.data?.addOns || []);
      addOnsLoadedRef.current = true;
    } catch (err) {
      console.error('Add-ons error:', err);
      setAvailableAddOns([]);
    }
  }, []);

  const fetchShopifyImagesForProducts = useCallback(async (productList) => {
    const idsArray = collectShopifyIdsFromProducts(productList);
    if (idsArray.length === 0) return;

    try {
      const response = await shopifyApi.getProductsByIds(idsArray);
      mergeShopifyProducts(response.products || []);
    } catch (err) {
      console.error('Failed to fetch Shopify product images:', err);
    }
  }, [mergeShopifyProducts]);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  useEffect(() => {
    let cancelled = false;
    productsAPI.getCategories({ includeInactive: 'true' })
      .then((response) => {
        if (!cancelled) setCategories(response.data || []);
      })
      .catch(() => {
        if (!cancelled) setError('Failed to load product categories');
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (products.length > 0) {
      fetchShopifyImagesForProducts(products);
    }
  }, [products, fetchShopifyImagesForProducts]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery.trim());
      setCurrentPage(1);
    }, 300);

    return () => clearTimeout(timeout);
  }, [searchQuery]);

  // Filtering and sorting happen on the server before pagination.
  useEffect(() => {
    setFilteredProducts(products || []);
  }, [products]);

  const fetchShopifyProducts = async ({
    searchQuery = '',
    append = false,
    cursor = null,
    excludeDkRental = true,
  } = {}) => {
    try {
      setLoadingShopify(true);
      const response = await shopifyApi.getProducts(searchQuery, {
        limit: SHOPIFY_PAGE_SIZE,
        cursor: append ? cursor : null,
        excludeDkRental: excludeDkRental && !searchQuery,
      });

      let pageProducts = response.products || [];
      if (excludeDkRental) {
        pageProducts = pageProducts.filter((p) => !(p.tags || []).includes('dk_rental'));
      }
      setModalShopifyProducts((prev) => (append ? [...prev, ...pageProducts] : pageProducts));
      mergeShopifyProducts(pageProducts);
      setShopifyModalCursor(response.pageInfo?.endCursor || null);
      setShopifyModalHasMore(Boolean(response.pageInfo?.hasNextPage));

      return pageProducts;
    } catch (err) {
      console.error('Failed to fetch Shopify products:', err);
      if (!append) {
        setModalShopifyProducts([]);
      }
      setShopifyModalHasMore(false);
      return [];
    } finally {
      setLoadingShopify(false);
    }
  };

  const searchBundleComponentProducts = useCallback(async (query, categoryIds, setter) => {
    if (!query || query.length < 2) {
      setter([]);
      return;
    }
    try {
      const response = await shopifyApi.getProducts(query, { limit: 20 });
      setter(filterByCategoryTags(response.products || [], categoryIds));
    } catch (err) {
      console.error('Bundle component search failed:', err);
      setter([]);
    }
  }, []);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Bundle product selection handlers
  const handleJacketSelect = (product) => {
    setSelectedJacket(product);
    setJacketSearchQuery(product ? product.title : '');
  };

  const handlePantsSelect = (product) => {
    setSelectedPants(product);
    setPantsSearchQuery(product ? product.title : '');
  };

  const getFilteredJackets = () => jacketProducts.slice(0, 10);
  const getFilteredPants = () => pantsProducts.slice(0, 10);

  useEffect(() => {
    if (!modalActive || !formData.isBundle) return undefined;
    const timeout = setTimeout(() => {
      searchBundleComponentProducts(jacketSearchQuery, JACKET_CATEGORY_IDS, setJacketProducts);
    }, 300);
    return () => clearTimeout(timeout);
  }, [jacketSearchQuery, modalActive, formData.isBundle, searchBundleComponentProducts]);

  useEffect(() => {
    if (!modalActive || !formData.isBundle) return undefined;
    const timeout = setTimeout(() => {
      searchBundleComponentProducts(pantsSearchQuery, PANTS_CATEGORY_IDS, setPantsProducts);
    }, 300);
    return () => clearTimeout(timeout);
  }, [pantsSearchQuery, modalActive, formData.isBundle, searchBundleComponentProducts]);

  const handlePriceStageChange = (stageNumber, field, value) => {
    setFormData(prev => ({
      ...prev,
      [`priceStage${stageNumber}`]: {
        ...prev[`priceStage${stageNumber}`],
        [field]: value
      }
    }));
  };

  const handleShopifyProductSelect = (productId, isSelected) => {
    if (isSelected) {
      setSelectedShopifyProducts(prev => [...prev, productId]);
    } else {
      setSelectedShopifyProducts(prev => prev.filter(id => id !== productId));
      // Remove variant selection for this product
      setSelectedVariants(prev => {
        const newVariants = { ...prev };
        delete newVariants[productId];
        return newVariants;
      });
    }
  };

  const handleVariantSelect = (productId, variantId) => {
    setSelectedVariants(prev => ({
      ...prev,
      [productId]: variantId
    }));
  };

  const handleShopifySearch = (query) => {
    setShopifySearchQuery(query);
    if (shopifySearchTimeoutRef.current) {
      clearTimeout(shopifySearchTimeoutRef.current);
    }
    shopifySearchTimeoutRef.current = setTimeout(() => {
      fetchShopifyProducts({ searchQuery: query, append: false, excludeDkRental: true });
    }, 300);
  };

  const handleLoadMoreShopifyProducts = () => {
    if (!shopifyModalHasMore || loadingShopify) return;
    fetchShopifyProducts({
      searchQuery: shopifySearchQuery,
      append: true,
      cursor: shopifyModalCursor,
      excludeDkRental: !shopifySearchQuery,
    });
  };

  // Bulk selection handlers
  const handleBulkSelect = (productId, isSelected) => {
    if (isSelected) {
      setSelectedProductIds(prev => [...prev, productId]);
    } else {
      setSelectedProductIds(prev => prev.filter(id => id !== productId));
    }
  };

  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedProductIds(filteredProducts.map(product => product.id));
    } else {
      setSelectedProductIds([]);
    }
  };

  const handleBulkToggleActive = async (action) => {
    if (selectedProductIds.length === 0) return;
    
    if (window.confirm(`Are you sure you want to ${action} ${selectedProductIds.length} products?`)) {
      try {
        const apiMethod = action === 'deactivate' ? productsAPI.deactivate : productsAPI.activate;
        await Promise.all(
          selectedProductIds.map(id => apiMethod(id))
        );
        setSelectedProductIds([]);
        fetchProducts();
      } catch (err) {
        setError(`Failed to ${action} some products`);
      }
    }
  };



  // Helper function to check if a field has different values across selected products
  const getFieldDifferences = (fieldName, selectedProducts) => {
    if (!Array.isArray(selectedProducts) || selectedProducts.length <= 1) return false;
    
    const values = selectedProducts.map(product => {
      if (fieldName.includes('.')) {
        const [parent, child] = fieldName.split('.');
        const parentValue = product[parent];
        if (!parentValue) return null;
        return parentValue[child];
      }
      return product[fieldName];
    }).filter(value => value !== undefined && value !== null);
    
    // If all values are null/undefined, no differences
    if (values.length === 0) return false;
    
    return new Set(values).size > 1;
  };

  // Helper function to get field styling for bulk edit
  const getFieldStyle = (fieldName, selectedProducts) => {
    if (!Array.isArray(selectedProducts) || selectedProducts.length <= 1) return {};
    
    const hasDifferences = getFieldDifferences(fieldName, selectedProducts);
    if (hasDifferences) {
      return {
        backgroundColor: '#f6f6f7', // Gray background
        border: '2px solid #ffd700', // Yellow outline for fields with different values
        borderRadius: '4px'
      };
    }
    return {};
  };

  const handleBulkEdit = async () => {
    if (selectedProductIds.length === 0) return;
    
    const selectedProducts = filteredProducts.filter(product => 
      selectedProductIds.includes(product.id)
    );

    const idsToFetch = collectShopifyIdsFromProducts(selectedProducts);
    let fetchedById = [];
    if (idsToFetch.length > 0) {
      const response = await shopifyApi.getProductsByIds(idsToFetch);
      fetchedById = response.products || [];
      mergeShopifyProducts(fetchedById);
      setModalShopifyProducts(fetchedById);
    }
    await fetchAddOns();
    
    // Get unique Shopify product IDs from selected products
    const uniqueShopifyProductIds = [...new Set(selectedProducts.map(p => p.shopifyProductId))].filter(Boolean);
    
    // Set up form data with representative values
    const representativeProduct = selectedProducts[0];
    const formData = {
      isBundle: representativeProduct.isBundle || false,
      shopifyProductId: representativeProduct.shopifyProductId || '',
      shopifyVariantId: representativeProduct.shopifyVariantId || '',
      title: representativeProduct.title,
      priceStage1: representativeProduct.priceStage1 || { enabled: false, price: '', minDays: 0 },
      priceStage2: representativeProduct.priceStage2 || { enabled: false, price: '', minDays: 4 },
      priceStage3: representativeProduct.priceStage3 || { enabled: false, price: '', minDays: 7 },
      priceStage4: representativeProduct.priceStage4 || { enabled: false, price: '', minDays: 14 },
      securityDeposit: representativeProduct.securityDeposit?.toString() || '',
      minimumRentalDays: representativeProduct.minimumRentalDays.toString(),
      bufferBefore: (representativeProduct.bufferBefore ?? 3).toString(),
      bufferAfter: (representativeProduct.bufferAfter ?? 4).toString(),
      totalQuantity: representativeProduct.totalQuantity.toString(),
      categories: representativeProduct.categories?.join(', ') || '',
      tags: representativeProduct.tags?.join(', ') || '',
    };
    
    // Set bundle products for bulk edit (use representative product)
    if (representativeProduct.jacketProductId) {
      const jacketProduct = fetchedById.find((p) => p.id.toString() === representativeProduct.jacketProductId);
      if (jacketProduct) {
        setSelectedJacket(jacketProduct);
        setJacketSearchQuery(jacketProduct.title || '');
      } else {
        setSelectedJacket(null);
        setJacketSearchQuery('');
      }
    } else {
      setSelectedJacket(null);
      setJacketSearchQuery('');
    }
    
    if (representativeProduct.pantsProductId) {
      const pantsProduct = fetchedById.find((p) => p.id.toString() === representativeProduct.pantsProductId);
      if (pantsProduct) {
        setSelectedPants(pantsProduct);
        setPantsSearchQuery(pantsProduct.title || '');
      } else {
        setSelectedPants(null);
        setPantsSearchQuery('');
      }
    } else {
      setSelectedPants(null);
      setPantsSearchQuery('');
    }
    
    // Set up selected variants for bulk edit - pre-select the current variants
    const selectedVariantsForBulkEdit = {};
    selectedProducts.forEach(product => {
      if (product.shopifyVariantId) {
        selectedVariantsForBulkEdit[product.shopifyProductId] = product.shopifyVariantId;
      }
    });
    
    setFormData(formData);
    setSelectedShopifyProducts(uniqueShopifyProductIds.map(id => id.toString())); // Set Shopify product IDs
    setSelectedVariants(selectedVariantsForBulkEdit); // Set current variants as selected
    setEditingProduct(selectedProducts); // Set to array of products for bulk edit
    setModalActive(true);
  };



  const resetForm = () => {
    setFormData({
      isBundle: false,
      shopifyProductId: '',
      shopifyVariantId: '',
      title: '',
      priceStage1: { enabled: true, price: '33', minDays: 0 },
      priceStage2: { enabled: true, price: '28', minDays: 4 },
      priceStage3: { enabled: true, price: '22', minDays: 7 },
      priceStage4: { enabled: false, price: '', minDays: 14 },
      securityDeposit: '0',
      minimumRentalDays: '3',
      bufferBefore: '3',
      bufferAfter: '4',
      totalQuantity: '',
      categories: '',
      tags: '',
    });
    setSelectedShopifyProducts([]);
    setSelectedVariants({});
    setSelectedAddOns([]);
    setAddOnSettings({});
    setSelectedJacket(null);
    setSelectedPants(null);
    setJacketSearchQuery('');
    setPantsSearchQuery('');
    setShopifySearchQuery('');
    setModalShopifyProducts([]);
    setShopifyModalCursor(null);
    setShopifyModalHasMore(false);
    setJacketProducts([]);
    setPantsProducts([]);
    setEditingProduct(null);
  };

  // Rental booking functions
  const openRentalModal = (product) => {
    setSelectedProduct(product);
    setRentalModalActive(true);
  };

  const handleRentalSuccess = () => {
    fetchProducts(); // Refresh products to update availability
  };

  const handleModalClose = () => {
    setModalActive(false);
    resetForm();
  };

  const handleAddProduct = async () => {
    setShopifySearchQuery('');
    setShopifyModalCursor(null);
    setModalShopifyProducts([]);
    await fetchShopifyProducts({ searchQuery: '', excludeDkRental: true });
    await fetchAddOns();
    setModalActive(true);
  };

  const handleEdit = async (product) => {
    const idsToFetch = collectShopifyIdsFromProducts([product]);
    let fetchedById = [];
    if (idsToFetch.length > 0) {
      const response = await shopifyApi.getProductsByIds(idsToFetch);
      fetchedById = response.products || [];
      mergeShopifyProducts(fetchedById);
    }

    setShopifySearchQuery('');
    setModalShopifyProducts(fetchedById);
    setShopifyModalHasMore(false);
    await fetchAddOns();
    
    setEditingProduct(product);
    setFormData({
      isBundle: product.isBundle || false,
      shopifyProductId: product.shopifyProductId || '',
      shopifyVariantId: product.shopifyVariantId || '',
      title: product.title,
      priceStage1: product.priceStage1 || { enabled: true, price: '33', minDays: 0 },
      priceStage2: product.priceStage2 || { enabled: true, price: '28', minDays: 4 },
      priceStage3: product.priceStage3 || { enabled: true, price: '22', minDays: 7 },
      priceStage4: product.priceStage4 || { enabled: false, price: '', minDays: 14 },
      securityDeposit: product.securityDeposit?.toString() || '0',
      minimumRentalDays: product.minimumRentalDays.toString(),
      bufferBefore: (product.bufferBefore ?? 3).toString(),
      bufferAfter: (product.bufferAfter ?? 4).toString(),
      totalQuantity: product.totalQuantity.toString(),
      categories: product.categories?.join(', ') || '',
      tags: product.tags?.join(', ') || '',
    });
    
    if (product.jacketProductId) {
      const jacketProduct = fetchedById.find((p) => p.id.toString() === product.jacketProductId)
        || shopifyProductLookup[product.jacketProductId];
      if (jacketProduct) {
        setSelectedJacket(jacketProduct);
        setJacketSearchQuery(jacketProduct.title || '');
      } else {
        setSelectedJacket(null);
        setJacketSearchQuery('');
      }
    } else {
      setSelectedJacket(null);
      setJacketSearchQuery('');
    }
    
    if (product.pantsProductId) {
      const pantsProduct = fetchedById.find((p) => p.id.toString() === product.pantsProductId)
        || shopifyProductLookup[product.pantsProductId];
      if (pantsProduct) {
        setSelectedPants(pantsProduct);
        setPantsSearchQuery(pantsProduct.title || '');
      } else {
        setSelectedPants(null);
        setPantsSearchQuery('');
      }
    } else {
      setSelectedPants(null);
      setPantsSearchQuery('');
    }
    
    // Set the selected Shopify product for edit mode
    if (product.shopifyProductId) {
      setSelectedShopifyProducts([product.shopifyProductId]);
      if (product.shopifyVariantId) {
        setSelectedVariants({ [product.shopifyProductId]: product.shopifyVariantId });
      }
    } else {
      setSelectedShopifyProducts([]);
      setSelectedVariants({});
    }
    
    // Load existing add-ons for this product
    loadProductAddOns(product.id);
    
    setModalActive(true);
  };

  const loadProductAddOns = async (productId) => {
    try {
      const response = await addOnsAPI.getByProduct(productId);
      const addOns = response.data?.addOns || [];
      
      // Extract add-on IDs and their settings
      const addOnIds = addOns.map(addOn => addOn.id);
      const settings = {};
      
      addOns.forEach(addOn => {
        const linkData = addOn.addOnProducts?.[0]; // Get the junction table data
        if (linkData) {
          settings[addOn.id] = {
            isRequired: linkData.isRequired || false,
            displayOrder: linkData.displayOrder || 0
          };
        }
      });
      
      setSelectedAddOns(addOnIds);
      setAddOnSettings(settings);
    } catch (err) {
      console.error('Error loading product add-ons:', err);
      setSelectedAddOns([]);
      setAddOnSettings({});
    }
  };

  // Add-on management functions
  const handleAddOnToggle = (addOnId, isSelected) => {
    setSelectedAddOns(prev => {
      if (isSelected) {
        // Initialize settings for newly selected add-on
        setAddOnSettings(prevSettings => ({
          ...prevSettings,
          [addOnId]: {
            isRequired: prevSettings[addOnId]?.isRequired || false,
            displayOrder: prevSettings[addOnId]?.displayOrder || 0
          }
        }));
        return [...prev, addOnId];
      } else {
        // Remove from settings as well
        setAddOnSettings(prevSettings => {
          const newSettings = { ...prevSettings };
          delete newSettings[addOnId];
          return newSettings;
        });
        return prev.filter(id => id !== addOnId);
      }
    });
  };

  const handleAddOnSettingChange = (addOnId, setting, value) => {
    setAddOnSettings(prev => ({
      ...prev,
      [addOnId]: {
        isRequired: false,
        displayOrder: 0,
        ...prev[addOnId],
        [setting]: value
      }
    }));
  };

  const updateProductAddOnLinks = async (productId) => {
    try {
      // Get current add-ons for this product to see what needs to be removed
      const currentResponse = await addOnsAPI.getByProduct(productId);
      const currentAddOns = currentResponse.data?.addOns || [];
      const currentAddOnIds = currentAddOns.map(addOn => addOn.id);
      
      // Remove add-ons that are no longer selected
      const toRemove = currentAddOnIds.filter(id => !selectedAddOns.includes(id));
      for (const addOnId of toRemove) {
        await addOnsAPI.unlinkFromProduct(addOnId, productId);
      }
      
      // Add or update selected add-ons
      for (const addOnId of selectedAddOns) {
        const settings = addOnSettings[addOnId] || {};
        const linkData = {
          isRequired: settings.isRequired || false,
          displayOrder: settings.displayOrder || 0
        };
        
        try {
          if (currentAddOnIds.includes(addOnId)) {
            // Update existing link (we need an update endpoint or recreate)
            await addOnsAPI.unlinkFromProduct(addOnId, productId);
            await addOnsAPI.linkToProduct(addOnId, productId, linkData);
          } else {
            // Create new link
            await addOnsAPI.linkToProduct(addOnId, productId, linkData);
          }
        } catch (err) {
          // If link already exists, try to update it
          console.warn(`Add-on ${addOnId} might already be linked to product ${productId}`);
        }
      }
    } catch (err) {
      console.error('Error updating product add-on links:', err);
    }
  };

  const handleSubmit = async () => {
    try {
      if (Array.isArray(editingProduct)) {
        // Bulk edit multiple products - only update fields that should be changed
        const productData = {
          isBundle: formData.isBundle,
          // Don't update shopifyProductId, shopifyVariantId, or title - keep existing values
          priceStage1: formData.priceStage1.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage1.price),
            minDays: parseInt(formData.priceStage1.minDays)
          } : null,
          priceStage2: formData.priceStage2.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage2.price),
            minDays: parseInt(formData.priceStage2.minDays)
          } : null,
          priceStage3: formData.priceStage3.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage3.price),
            minDays: parseInt(formData.priceStage3.minDays)
          } : null,
          priceStage4: formData.priceStage4.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage4.price),
            minDays: parseInt(formData.priceStage4.minDays)
          } : null,
          securityDeposit: formData.securityDeposit ? parseFloat(formData.securityDeposit) : '0',
          minimumRentalDays: parseInt(formData.minimumRentalDays),
          bufferBefore: parseInt(formData.bufferBefore, 10),
          bufferAfter: parseInt(formData.bufferAfter, 10),
          buffer: parseInt(formData.bufferBefore, 10) + parseInt(formData.bufferAfter, 10),
          totalQuantity: parseInt(formData.totalQuantity),
          categories: formData.categories ? formData.categories.split(',').map(c => c.trim()) : [],
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
          jacketProductId: selectedJacket?.id || null,
          pantsProductId: selectedPants?.id || null,
        };
        
        // Update all selected products
        await Promise.all(
          editingProduct.map(product => productsAPI.update(product.id, productData))
        );
        
        setSelectedProductIds([]);
      } else if (editingProduct) {
        // Single product edit
        console.log('🔍 DEBUG v1.58 - Selected jacket:', selectedJacket);
        console.log('🔍 DEBUG v1.58 - Selected pants:', selectedPants);
        console.log('🔍 DEBUG v1.58 - Jacket id:', selectedJacket?.id);
        console.log('🔍 DEBUG v1.58 - Pants id:', selectedPants?.id);
        
        const productData = {
          isBundle: formData.isBundle,
          shopifyProductId: formData.shopifyProductId,
          shopifyVariantId: formData.shopifyVariantId || null,
          title: formData.title,
          priceStage1: formData.priceStage1.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage1.price),
            minDays: parseInt(formData.priceStage1.minDays)
          } : null,
          priceStage2: formData.priceStage2.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage2.price),
            minDays: parseInt(formData.priceStage2.minDays)
          } : null,
          priceStage3: formData.priceStage3.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage3.price),
            minDays: parseInt(formData.priceStage3.minDays)
          } : null,
          priceStage4: formData.priceStage4.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage4.price),
            minDays: parseInt(formData.priceStage4.minDays)
          } : null,
          securityDeposit: formData.securityDeposit ? parseFloat(formData.securityDeposit) : '0',
          minimumRentalDays: parseInt(formData.minimumRentalDays),
          bufferBefore: parseInt(formData.bufferBefore, 10),
          bufferAfter: parseInt(formData.bufferAfter, 10),
          buffer: parseInt(formData.bufferBefore, 10) + parseInt(formData.bufferAfter, 10),
          totalQuantity: parseInt(formData.totalQuantity),
          categories: formData.categories ? formData.categories.split(',').map(c => c.trim()) : [],
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
          jacketProductId: selectedJacket?.id || null,
          pantsProductId: selectedPants?.id || null,
        };
        
        console.log('🔍 DEBUG v1.58 - Product data being sent:', productData);
        await productsAPI.update(editingProduct.id, productData);
        
        // Update add-on links for single product edit
        await updateProductAddOnLinks(editingProduct.id);
      } else if (selectedShopifyProducts.length > 0) {
        // Batch create from selected Shopify products - create separate entries for each variant
        const productsToCreate = [];
        
        for (const productId of selectedShopifyProducts) {
          const shopifyProduct = shopifyProducts.find(p => p.id.toString() === productId);
          if (!shopifyProduct) continue;
          
          // Create a separate rental product for each variant (including default)
          const variantsToCreate = shopifyProduct.variants || [];
          
          for (const variant of variantsToCreate) {
            const productData = {
              isBundle: formData.isBundle,
              shopifyProductId: productId,
              shopifyVariantId: variant.id.toString(),
              title: `${shopifyProduct.title} - ${variant.title}`,
              priceStage1: formData.priceStage1.enabled ? {
                enabled: true,
                price: parseFloat(formData.priceStage1.price),
                minDays: parseInt(formData.priceStage1.minDays)
              } : null,
              priceStage2: formData.priceStage2.enabled ? {
                enabled: true,
                price: parseFloat(formData.priceStage2.price),
                minDays: parseInt(formData.priceStage2.minDays)
              } : null,
              priceStage3: formData.priceStage3.enabled ? {
                enabled: true,
                price: parseFloat(formData.priceStage3.price),
                minDays: parseInt(formData.priceStage3.minDays)
              } : null,
              priceStage4: formData.priceStage4.enabled ? {
                enabled: true,
                price: parseFloat(formData.priceStage4.price),
                minDays: parseInt(formData.priceStage4.minDays)
              } : null,
              securityDeposit: formData.securityDeposit ? parseFloat(formData.securityDeposit) : 0,
              minimumRentalDays: parseInt(formData.minimumRentalDays),
              bufferBefore: parseInt(formData.bufferBefore, 10),
              bufferAfter: parseInt(formData.bufferAfter, 10),
              buffer: parseInt(formData.bufferBefore, 10) + parseInt(formData.bufferAfter, 10),
              totalQuantity: parseInt(variant.inventoryQuantity || 1),
              categories: formData.categories ? formData.categories.split(',').map(c => c.trim()) : (shopifyProduct.productType ? [shopifyProduct.productType] : []),
              tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : (shopifyProduct.tags || []),
              jacketProductId: selectedJacket?.id || null,
              pantsProductId: selectedPants?.id || null,
            };
            
            productsToCreate.push(productData);
          }
        }
        
        const createdProducts = await productsAPI.createBatch(productsToCreate);
        
        // Add "dk_rental" tag to Shopify products (only once per unique product ID)
        const uniqueShopifyProductIds = [...new Set(selectedShopifyProducts)];
        for (const productId of uniqueShopifyProductIds) {
          try {
            await shopifyApi.addProductTags(productId, ['dk_rental']);
            console.log(`✅ Added 'dk_rental' tag to Shopify product ${productId}`);
          } catch (err) {
            console.error(`Failed to add 'dk_rental' tag to product ${productId}:`, err);
            // Don't fail the whole operation if tag update fails
          }
        }
        
        // Link add-ons to all newly created products
        if (selectedAddOns.length > 0 && createdProducts.data?.products) {
          for (const product of createdProducts.data.products) {
            await updateProductAddOnLinks(product.id);
          }
        }
      } else {
        // Single product create (legacy)
        const productData = {
          isBundle: formData.isBundle,
          shopifyProductId: formData.shopifyProductId,
          shopifyVariantId: formData.shopifyVariantId || null,
          title: formData.title,
          priceStage1: formData.priceStage1.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage1.price),
            minDays: parseInt(formData.priceStage1.minDays)
          } : null,
          priceStage2: formData.priceStage2.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage2.price),
            minDays: parseInt(formData.priceStage2.minDays)
          } : null,
          priceStage3: formData.priceStage3.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage3.price),
            minDays: parseInt(formData.priceStage3.minDays)
          } : null,
          priceStage4: formData.priceStage4.enabled ? {
            enabled: true,
            price: parseFloat(formData.priceStage4.price),
            minDays: parseInt(formData.priceStage4.minDays)
          } : null,
          securityDeposit: formData.securityDeposit ? parseFloat(formData.securityDeposit) : 0,
          minimumRentalDays: parseInt(formData.minimumRentalDays),
          bufferBefore: parseInt(formData.bufferBefore, 10),
          bufferAfter: parseInt(formData.bufferAfter, 10),
          buffer: parseInt(formData.bufferBefore, 10) + parseInt(formData.bufferAfter, 10),
          totalQuantity: parseInt(formData.totalQuantity),
          categories: formData.categories ? formData.categories.split(',').map(c => c.trim()) : [],
          tags: formData.tags ? formData.tags.split(',').map(t => t.trim()) : [],
          jacketProductId: selectedJacket?.id || null,
          pantsProductId: selectedPants?.id || null,
        };
        const createdProduct = await productsAPI.create(productData);
        
        // Add "dk_rental" tag to Shopify product
        if (formData.shopifyProductId) {
          try {
            await shopifyApi.addProductTags(formData.shopifyProductId, ['dk_rental']);
            console.log(`✅ Added 'dk_rental' tag to Shopify product ${formData.shopifyProductId}`);
          } catch (err) {
            console.error(`Failed to add 'dk_rental' tag to product ${formData.shopifyProductId}:`, err);
            // Don't fail the whole operation if tag update fails
          }
        }
        
        // Link add-ons to newly created product
        if (selectedAddOns.length > 0 && createdProduct.data?.id) {
          await updateProductAddOnLinks(createdProduct.data.id);
        }
      }

      handleModalClose();
      fetchProducts();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save product');
    }
  };

  const handleToggleActive = async (productId, isActive) => {
    const action = isActive ? 'deactivate' : 'activate';
    if (window.confirm(`Are you sure you want to ${action} this product?`)) {
      try {
        if (isActive) {
          await productsAPI.deactivate(productId);
        } else {
          await productsAPI.activate(productId);
        }
        fetchProducts();
      } catch (err) {
        setError(`Failed to ${action} product`);
      }
    }
  };

  const openConditionsModal = async (product) => {
    setConditionProduct(product);
    setConditionRecords([]);
    setConditionError('');
    setConditionModalOpen(true);
    try {
      setConditionsLoading(true);
      const response = await productsAPI.getConditions(product.id);
      setConditionRecords(response.data?.conditions || []);
      setConditionProduct((current) => ({
        ...current,
        totalQuantity: response.data?.totalQuantity ?? current.totalQuantity
      }));
    } catch (err) {
      setConditionError(err.response?.data?.error || 'Failed to load condition records');
    } finally {
      setConditionsLoading(false);
    }
  };

  const addConditionRecord = () => {
    if (!conditionProduct || conditionRecords.length >= conditionProduct.totalQuantity) return;
    setConditionRecords((records) => [
      ...records,
      { clientId: `${Date.now()}-${Math.random()}`, condition: 'new', status: 'available', comment: '' }
    ]);
  };

  const updateConditionRecord = (index, field, value) => {
    setConditionRecords((records) => records.map((record, recordIndex) =>
      recordIndex === index ? { ...record, [field]: value } : record
    ));
  };

  const saveConditionRecords = async () => {
    if (!conditionProduct) return;
    try {
      setConditionsSaving(true);
      setConditionError('');
      const payload = conditionRecords.map(({ id, condition, status, comment }) => ({
        ...(id != null ? { id } : {}),
        condition,
        status: status || 'available',
        comment
      }));
      await productsAPI.saveConditions(conditionProduct.id, payload);
      await fetchProducts();
      setConditionModalOpen(false);
      setConditionProduct(null);
      setConditionRecords([]);
    } catch (err) {
      setConditionError(err.response?.data?.error || 'Failed to save condition records');
    } finally {
      setConditionsSaving(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const getStatusBadge = (isActive) => {
    return isActive ? 
      <Badge status="success">Active</Badge> : 
      <Badge status="warning">Inactive</Badge>;
  };

  const getProductImage = useCallback((product) => {
    if (!product.shopifyProductId) return '/placeholder.png';
    const shopifyProduct = shopifyProductLookup[product.shopifyProductId];
    if (shopifyProduct?.images?.length > 0) {
      return shopifyProduct.images[0];
    }
    return '/placeholder.png';
  }, [shopifyProductLookup]);

  if (loading && (!products || products.length === 0)) {
    return (
      <Layout>
        <Layout.Section>
          <Card sectioned>
            <InlineStack align="center" gap="400">
              <Spinner size="large" />
              <Text variant="bodyMd">Loading products...</Text>
            </InlineStack>
          </Card>
        </Layout.Section>
      </Layout>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: '20px' }}>
        <Text variant="headingLg" as="h1">Rental Products</Text>
      </div>
      {error && (
        <Layout>
          <Layout.Section>
            <Banner status="critical" onDismiss={() => setError('')}>
              {error}
            </Banner>
          </Layout.Section>
        </Layout>
      )}

      <Layout>
        <Layout.Section>
          {/* Search and Filter Controls */}
          <Card sectioned>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text variant="headingMd">
                  Products ({totalProducts})
                  {bundleFilter === 'bundles' && ' - Bundles Only'}
                  {bundleFilter === 'non-bundles' && ' - Individual Items'}
                  {bundleFilter === 'all' && ' - All Products'}
                </Text>
                <InlineStack gap="200">
                  <Button onClick={handleAddProduct}>Add Product</Button>
                </InlineStack>
              </InlineStack>
              
              <>
                  <InlineStack gap="400" wrap>
                    <div style={{ minWidth: '300px' }}>
                      <TextField
                        label="Search products"
                        value={searchQuery}
                        onChange={setSearchQuery}
                        placeholder="Search by name or tags..."
                        clearButton
                        onClearButtonClick={() => setSearchQuery('')}
                      />
                    </div>
                    
                    <div style={{ minWidth: '200px' }}>
                      <Select
                        label="Status"
                        options={[
                          { label: 'All Products', value: 'all' },
                          { label: 'Active', value: 'active' },
                          { label: 'Inactive', value: 'inactive' },
                        ]}
                        value={statusFilter}
                        onChange={(value) => {
                          setStatusFilter(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    
                    <div style={{ minWidth: '200px' }}>
                      <Select
                        label="Product Type"
                        options={[
                          { label: 'Bundles Only', value: 'bundles' },
                          { label: 'Individual Items', value: 'non-bundles' },
                          { label: 'All Products', value: 'all' },
                        ]}
                        value={bundleFilter}
                        onChange={(value) => {
                          setBundleFilter(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                    
                    <div style={{ minWidth: '200px' }}>
                      <Select
                        label="Category"
                        options={[
                          { label: 'All categories', value: '' },
                          ...categories.map((category) => ({ label: category, value: category })),
                        ]}
                        value={categoryFilter}
                        onChange={(value) => {
                          setCategoryFilter(value);
                          setCurrentPage(1);
                        }}
                      />
                    </div>

                    <div style={{ minWidth: '200px' }}>
                      <Select
                        label="Sort by"
                        options={[
                          { label: 'Category, name, size', value: 'category-asc' },
                          { label: 'Title A-Z', value: 'title-asc' },
                          { label: 'Title Z-A', value: 'title-desc' },
                          { label: 'Price Low-High', value: 'price-asc' },
                          { label: 'Price High-Low', value: 'price-desc' },
                          { label: 'Total Quantity', value: 'quantity-desc' },
                          { label: 'Date Created', value: 'created-desc' },
                        ]}
                        value={`${sortBy}-${sortDirection}`}
                        onChange={(value) => {
                          const [field, direction] = value.split('-');
                          setSortBy(field);
                          setSortDirection(direction);
                          setCurrentPage(1);
                        }}
                      />
                    </div>
                  </InlineStack>
                </>
            </BlockStack>
          </Card>

          {(products && products.length > 0) ? (
            <>
              {/* Bulk Actions */}
              {selectedProductIds.length > 0 && (
                <Card sectioned>
                  <InlineStack align="space-between">
                    <Text variant="bodyMd" fontWeight="semibold">
                      {selectedProductIds.length} product{selectedProductIds.length > 1 ? 's' : ''} selected
                    </Text>
                    <InlineStack gap="200">
                      <Button 
                        variant="secondary"
                        onClick={handleBulkEdit}
                        disabled={selectedProductIds.length === 0}
                      >
                        Bulk Edit
                      </Button>
                      <Button 
                        variant="secondary"
                        tone="critical"
                        onClick={() => handleBulkToggleActive('deactivate')}
                        disabled={selectedProductIds.length === 0}
                      >
                        Bulk Deactivate
                      </Button>
                      <Button 
                        variant="secondary"
                        tone="success"
                        onClick={() => handleBulkToggleActive('activate')}
                        disabled={selectedProductIds.length === 0}
                      >
                        Bulk Activate
                      </Button>
                      <Button 
                        variant="plain"
                        onClick={() => setSelectedProductIds([])}
                      >
                        Clear Selection
                      </Button>
                    </InlineStack>
                  </InlineStack>
                </Card>
              )}

              {/* Products DataTable */}
              <Card>
                <DataTable
                  columnContentTypes={['text', 'text', 'text', 'text', 'text', 'text', 'text']}
                  headings={[
                    <Checkbox
                      key="select-all"
                      checked={selectedProductIds.length === (filteredProducts || []).length && (filteredProducts || []).length > 0}
                      onChange={handleSelectAll}
                    />,
                    'Product',
                    'Status',
                    'Stock',
                    'Pricing',
                    'Rental Terms',
                    'Actions'
                  ]}
                  rows={filteredProducts.map((product) => [
                    // Checkbox column
                    <Checkbox
                      key={`checkbox-${product.id}`}
                      checked={selectedProductIds.includes(product.id)}
                      onChange={(checked) => handleBulkSelect(product.id, checked)}
                    />,
                    
                    // Product column with image and title
                    <InlineStack key={`product-${product.id}`} gap="300" align="start">
                      <img
                        src={getProductImage(product)}
                        alt={product.title}
                        loading="lazy"
                        decoding="async"
                        width={48}
                        height={48}
                        style={{
                          width: '48px',
                          height: '48px',
                          objectFit: 'cover',
                          borderRadius: '4px',
                          border: '1px solid #e1e3e5'
                        }}
                      />
                      <div style={{ minWidth: 0, maxWidth: 240 }}>
                        <BlockStack gap="100">
                          <div
                            title={product.title}
                            style={{
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              overflow: 'hidden',
                              overflowWrap: 'anywhere',
                              whiteSpace: 'normal',
                              lineHeight: '20px',
                              maxHeight: '40px',
                            }}
                          >
                            <Text variant="bodyMd" fontWeight="semibold">{product.title}</Text>
                          </div>
                          <InlineStack gap="100">
                            {product.isBundle && <Badge size="small" status="info">Bundle</Badge>}
                            {product.shopifyVariantId && <Badge size="small" tone="info">Variant</Badge>}
                          </InlineStack>
                        </BlockStack>
                      </div>
                    </InlineStack>,
                    
                    // Status
                    getStatusBadge(product.isActive),
                    
                    // Total physical inventory. Date availability is calculated elsewhere.
                    <BlockStack key={`stock-${product.id}`} gap="050">
                      <Text variant="bodyMd">{product.totalQuantity || 0} total</Text>
                      <Text variant="bodySm" tone="subdued">
                        {product.rentableQuantity ?? product.totalQuantity ?? 0} rentable · {product.unavailableQuantity || 0} unavailable
                      </Text>
                    </BlockStack>,
                    
                    // Price Range
                    <Text key={`price-${product.id}`} variant="bodyMd">
                      {(() => {
                        const enabledStages = [product.priceStage1, product.priceStage2, product.priceStage3, product.priceStage4]
                          .filter(stage => stage && stage.enabled)
                          .sort((a, b) => a.minDays - b.minDays);
                        
                        if (enabledStages.length === 0) return 'No pricing';
                        if (enabledStages.length === 1) return formatCurrency(enabledStages[0].price);
                        
                        const minPrice = Math.min(...enabledStages.map(s => s.price));
                        const maxPrice = Math.max(...enabledStages.map(s => s.price));
                        return minPrice === maxPrice ? 
                          formatCurrency(minPrice) : 
                          `${formatCurrency(minPrice)} - ${formatCurrency(maxPrice)}`;
                      })()}
                    </Text>,
                    
                    // Rental terms
                    <BlockStack key={`terms-${product.id}`} gap="050">
                      <Text variant="bodySm">Min. {product.minimumRentalDays || 3} days</Text>
                      <Text variant="bodySm" tone="subdued">
                        Deposit: {product.securityDeposit ? formatCurrency(product.securityDeposit) : 'None'}
                      </Text>
                    </BlockStack>,
                    
                    // Actions
                    <div key={`actions-${product.id}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(2, max-content)', gap: 6 }}>
                      <Button 
                        size="slim"
                        variant="primary"
                        disabled={!product.isActive || (product.totalQuantity || 0) === 0}
                        onClick={() => openRentalModal(product)}
                      >
                        Book
                      </Button>
                      <Button 
                        size="slim"
                        variant="secondary"
                        onClick={() => handleEdit(product)}
                      >
                        Edit
                      </Button>
                      <Button
                        size="slim"
                        variant="secondary"
                        onClick={() => navigate(`/products/${product.shopifyProductId}/bookings`, { state: { productTitle: product.title } })}
                      >
                        Bookings
                      </Button>
                      <Button 
                        size="slim"
                        variant="secondary"
                        tone={product.isActive ? "critical" : "success"}
                        onClick={() => handleToggleActive(product.id, product.isActive)}
                      >
                        {product.isActive ? 'Deactivate' : 'Activate'}
                      </Button>
                      <Button
                        size="slim"
                        variant="secondary"
                        onClick={() => openConditionsModal(product)}
                      >
                        Conditions
                      </Button>
                    </div>
                  ])}
                  footerContent={`Showing ${(filteredProducts || []).length} on page ${currentPage} of ${totalPages} (${totalProducts} total)`}
                />
              </Card>
              
              {(totalPages > 1 || totalProducts > RENTAL_PAGE_SIZE) && (
                <div style={{ padding: '16px', textAlign: 'center' }}>
                  <Pagination
                    hasPrevious={currentPage > 1}
                    onPrevious={() => setCurrentPage(currentPage - 1)}
                    hasNext={currentPage < totalPages}
                    onNext={() => setCurrentPage(currentPage + 1)}
                    label={`Page ${currentPage} of ${totalPages}`}
                  />
                </div>
              )}
            </>
          ) : (
            <Card>
              <div style={{ padding: '16px', textAlign: 'center' }}>
                <Text variant="bodyMd" tone="subdued">
                  {searchQuery || categoryFilter || statusFilter !== 'all'
                    ? 'No products match the current search and filters.'
                    : 'No products found. Add your first rental product to get started.'}
                </Text>
                {bundleFilter === 'bundles' && (searchQuery || categoryFilter) && (
                  <Text variant="bodySm" tone="subdued">
                    To find individual item tags, select Individual Items or All Products under Product Type.
                  </Text>
                )}
              </div>
            </Card>
          )}
        </Layout.Section>
      </Layout>

      <Modal
        open={modalActive}
        onClose={handleModalClose}
        title={Array.isArray(editingProduct) ? `Edit ${editingProduct.length} Products` : editingProduct ? 'Edit Product' : selectedShopifyProducts.length > 0 ? `Add Products` : 'Add New Product'}
        primaryAction={{
          content: Array.isArray(editingProduct) ? `Update ${editingProduct.length} Products` : editingProduct ? 'Update' : selectedShopifyProducts.length > 0 ? (() => {
            const totalVariants = selectedShopifyProducts.reduce((total, productId) => {
              const product = shopifyProducts.find(p => p.id.toString() === productId);
              return total + (product?.variants?.length || 0);
            }, 0);
            return `Create ${totalVariants} Products`;
          })() : 'Create',
          onAction: handleSubmit,
        }}
        secondaryActions={[
          {
            content: 'Cancel',
            onAction: handleModalClose,
          },
        ]}
      >
        <Modal.Section>
          <Form onSubmit={handleSubmit}>
            <FormLayout>
              {Array.isArray(editingProduct) && editingProduct.length > 1 && (
                <Banner status="info">
                  Editing {editingProduct.length} products. Fields with different values across products are highlighted in gray. 
                  Note: Shopify product and variant information cannot be changed in bulk edit mode.
                </Banner>
              )}
              
              <FormLayout.Group>
                <div>
                  <Text variant="bodyMd" fontWeight="semibold" as="label">
                    {Array.isArray(editingProduct) ? 'Linked Shopify Products' : editingProduct ? 'Shopify Product' : 'Select Shopify Products'}
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p">
                    {Array.isArray(editingProduct) 
                      ? 'The selected products are linked to the Shopify products below. You can see which products are being edited.'
                      : editingProduct 
                        ? 'This product is linked to the Shopify product below. You can change the variant if needed.'
                        : 'Choose products to create rental products. Each variant of the selected products will be created as a separate rental product with the same pricing configuration below.'
                    }
                  </Text>
                  
                  {/* Search field for Shopify products */}
                  {!editingProduct && (
                    <div style={{ marginBottom: '16px' }}>
                      <TextField
                        label="Search Shopify products"
                        value={shopifySearchQuery}
                        onChange={handleShopifySearch}
                        placeholder="Type any word to search products..."
                        clearButton
                        onClearButtonClick={() => handleShopifySearch('')}
                      />
                    </div>
                  )}
                  {loadingShopify ? (
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                      <Spinner size="small" />
                      <Text variant="bodyMd" tone="subdued">Loading products...</Text>
                    </div>
                  ) : (
                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e1e3e5', borderRadius: '4px', padding: '8px' }}>
                      {shopifyProducts.length === 0 ? (
                        <div style={{ padding: '16px', textAlign: 'center' }}>
                          <Text variant="bodyMd" tone="subdued">No products available</Text>
                        </div>
                      ) : (
                        (editingProduct && selectedShopifyProducts.length > 0 
                          ? shopifyProducts.filter(product => selectedShopifyProducts.includes(product.id.toString()))
                          : shopifyProducts
                        ).map(product => (
                          <div key={product.id} style={{ marginBottom: '8px' }}>
                            <div
                              style={{
                                padding: '12px',
                                border: selectedShopifyProducts.includes(product.id.toString()) ? '2px solid #008060' : '1px solid #e1e3e5',
                                borderRadius: '4px',
                                backgroundColor: selectedShopifyProducts.includes(product.id.toString()) ? '#f0f9f7' : 'white',
                                transition: 'all 0.2s ease'
                              }}
                            >
                              <InlineStack gap="300" align="start">
                                <Checkbox
                                  checked={selectedShopifyProducts.includes(product.id.toString())}
                                  onChange={(checked) => handleShopifyProductSelect(product.id.toString(), checked)}
                                  disabled={editingProduct}
                                />
                                {product.images && product.images.length > 0 && (
                                  <img
                                    src={product.images[0]}
                                    alt={product.title}
                                    loading="lazy"
                                    decoding="async"
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      objectFit: 'cover',
                                      borderRadius: '4px',
                                      border: '1px solid #e1e3e5'
                                    }}
                                  />
                                )}
                                <BlockStack gap="100">
                                  <Text variant="bodyMd" fontWeight="semibold">
                                    {product.title}
                                  </Text>
                                  <Text variant="bodySm" tone="subdued">
                                    {product.productType} • {product.status}
                                  </Text>
                                  {product.tags && product.tags.length > 0 && (
                                    <InlineStack gap="100">
                                      {product.tags.slice(0, 3).map(tag => (
                                        <Badge key={tag} size="small">{tag}</Badge>
                                      ))}
                                    </InlineStack>
                                  )}
                                </BlockStack>
                              </InlineStack>
                            </div>
                            
                            {/* Show variants if this product is selected and has variants - in edit mode or bulk edit mode */}
                            {((editingProduct && !Array.isArray(editingProduct)) || (Array.isArray(editingProduct) && selectedShopifyProducts.includes(product.id.toString()))) && product.hasVariants && (
                              <div style={{ marginLeft: '20px', marginTop: '8px' }}>
                                <Text variant="bodySm" fontWeight="semibold" tone="subdued">
                                  {Array.isArray(editingProduct) ? 'Variants Being Edited:' : 'Select Variant:'}
                                </Text>
                                {product.variants.map(variant => {
                                  const isSelected = selectedVariants[product.id.toString()] === variant.id.toString();
                                  const isBeingEdited = Array.isArray(editingProduct) && editingProduct.some(p => p.shopifyProductId === product.id.toString() && p.shopifyVariantId === variant.id.toString());
                                  
                                  return (
                                    <div
                                      key={variant.id}
                                      style={{
                                        padding: '8px 12px',
                                        marginTop: '4px',
                                        border: isSelected || isBeingEdited ? '2px solid #008060' : '1px solid #e1e3e5',
                                        borderRadius: '4px',
                                        cursor: Array.isArray(editingProduct) ? 'default' : 'pointer',
                                        backgroundColor: isSelected || isBeingEdited ? '#f0f9f7' : '#f9f9f9',
                                        transition: 'all 0.2s ease',
                                        opacity: Array.isArray(editingProduct) && !isBeingEdited ? 0.6 : 1
                                      }}
                                      onClick={Array.isArray(editingProduct) ? undefined : () => handleVariantSelect(product.id.toString(), variant.id.toString())}
                                    >
                                    <InlineStack gap="200" align="center">
                                      {variant.image && (
                                        <img
                                          src={variant.image}
                                          alt={variant.title}
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            border: '1px solid #e1e3e5'
                                          }}
                                        />
                                      )}
                                      <BlockStack gap="50">
                                        <Text variant="bodySm" fontWeight="medium">
                                          {variant.title}
                                          {variant.isDefault && (
                                            <Badge size="small" tone="subdued" style={{ marginLeft: '8px' }}>
                                              Default
                                            </Badge>
                                          )}
                                        </Text>
                                        <Text variant="bodySm" tone="subdued">
                                          ${variant.price} • Qty: {variant.inventoryQuantity || 0}
                                        </Text>
                                      </BlockStack>
                                    </InlineStack>
                                  </div>
                                )})}
                              </div>
                            )}
                            
                            {/* Show variants that will be created in create mode */}
                            {!editingProduct && selectedShopifyProducts.includes(product.id.toString()) && product.variants && product.variants.length > 0 && (
                              <div style={{ marginLeft: '20px', marginTop: '8px' }}>
                                <Text variant="bodySm" fontWeight="semibold" tone="subdued">
                                  Variants to be created ({product.variants.length}):
                                </Text>
                                {product.variants.map(variant => (
                                  <div
                                    key={variant.id}
                                    style={{
                                      padding: '8px 12px',
                                      marginTop: '4px',
                                      border: '1px solid #e1e3e5',
                                      borderRadius: '4px',
                                      backgroundColor: '#f9f9f9'
                                    }}
                                  >
                                    <InlineStack gap="200" align="center">
                                      {variant.image && (
                                        <img
                                          src={variant.image}
                                          alt={variant.title}
                                          style={{
                                            width: '40px',
                                            height: '40px',
                                            objectFit: 'cover',
                                            borderRadius: '4px',
                                            border: '1px solid #e1e3e5'
                                          }}
                                        />
                                      )}
                                      <BlockStack gap="50">
                                        <Text variant="bodySm" fontWeight="medium">
                                          {variant.title}
                                        </Text>
                                        <Text variant="bodySm" tone="subdued">
                                          ${variant.price} • Qty: {variant.inventoryQuantity || 0}
                                        </Text>
                                      </BlockStack>
                                    </InlineStack>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                          </div>
                        ))
                      )}
                      {!editingProduct && shopifyModalHasMore && (
                        <div style={{ marginTop: '12px', textAlign: 'center' }}>
                          <Button
                            onClick={handleLoadMoreShopifyProducts}
                            loading={loadingShopify}
                            disabled={loadingShopify}
                          >
                            Load more products
                          </Button>
                        </div>
                      )}
                    </div>
                  )}
                  
                  {selectedShopifyProducts.length > 0 && (
                    <div style={{ marginTop: '12px', padding: '12px', backgroundColor: '#f6f6f7', borderRadius: '4px' }}>
                      <Text variant="bodyMd" fontWeight="semibold">
                        {editingProduct ? 'Selected Product:' : 'Products to Create:'}
                      </Text>
                      <div style={{ marginTop: '8px' }}>
                        {selectedShopifyProducts.map(productId => {
                          const product = shopifyProducts.find(p => p.id.toString() === productId);
                          if (!product) return null; // Skip if product not found
                          if (editingProduct) {
                            const variantId = selectedVariants[productId];
                            const variant = variantId ? product.variants.find(v => v.id.toString() === variantId) : null;
                            return (
                              <div key={productId} style={{ marginBottom: '4px' }}>
                                <Text variant="bodySm">
                                  • {product.title}
                                  {variant && ` - ${variant.title}`}
                                </Text>
                              </div>
                            );
                          } else {
                            // Show variants that will be created
                            const totalVariants = product.variants ? product.variants.length : 0;
                            return (
                              <div key={productId} style={{ marginBottom: '4px' }}>
                                <Text variant="bodySm">
                                  • {product.title} ({totalVariants} variant{totalVariants !== 1 ? 's' : ''})
                                </Text>
                                {product.variants && product.variants.map(variant => (
                                  <div key={variant.id} style={{ marginLeft: '16px', marginTop: '2px' }}>
                                    <Text variant="bodySm" tone="subdued">
                                      - {variant.title}
                                    </Text>
                                  </div>
                                ))}
                                {totalVariants === 0 && (
                                  <div style={{ marginLeft: '16px', marginTop: '2px' }}>
                                    <Text variant="bodySm" tone="subdued">
                                      - No variants (will be skipped)
                                    </Text>
                                  </div>
                                )}
                              </div>
                            )}
                          })}
                      </div>
                    </div>
                  )}
                </div>
              </FormLayout.Group>

              <Checkbox
                label="This is a bundle product"
                checked={formData.isBundle}
                onChange={(checked) => handleFormChange('isBundle', checked)}
                helpText="Bundle products are treated differently in the rental system"
                disabled={false}
                style={getFieldStyle('isBundle', editingProduct)}
              />

              {formData.isBundle && (
                <Banner status="info">
                  This product is tagged as "bundle" and will be treated as a bundle.
                </Banner>
              )}

              {/* Bundle-specific fields */}
              {formData.isBundle && (
                <div style={{ 
                  marginTop: '16px', 
                  padding: '16px', 
                  backgroundColor: '#f6f6f7', 
                  borderRadius: '8px', 
                  border: '1px solid #e1e3e5'
                }}>
                  <Text variant="headingMd" as="h4" style={{ marginBottom: '12px' }}>
                    Bundle Configuration
                  </Text>
                  <Text variant="bodySm" tone="subdued" as="p" style={{ marginBottom: '16px' }}>
                    Configure the specific items included in this bundle product.
                  </Text>
                  
                  <FormLayout.Group>
                    {/* Jacket Selection */}
                    <div>
                      <Text variant="bodySm" fontWeight="semibold" as="label" style={{ marginBottom: '8px', display: 'block' }}>
                        Jacket
                      </Text>
                      <div style={{ position: 'relative' }}>
                        <TextField
                          value={jacketSearchQuery}
                          onChange={setJacketSearchQuery}
                          placeholder="Search for jacket products..."
                          autoComplete="off"
                        />
                        {jacketSearchQuery && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            {getFilteredJackets().length > 0 ? (
                              getFilteredJackets().map(product => (
                                <div
                                  key={product.id}
                                  onClick={() => handleJacketSelect(product)}
                                  style={{
                                    padding: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f1f1f1',
                                    backgroundColor: selectedJacket?.id === product.id ? '#f0f9f7' : 'white'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f6f6f7'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = selectedJacket?.id === product.id ? '#f0f9f7' : 'white'}
                                >
                                  <div style={{ fontWeight: '500' }}>{product.title}</div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    {product.productType} • ${product.variants?.[0]?.price || 'N/A'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>
                                No jacket products found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {selectedJacket && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f0f9f7',
                          border: '1px solid #9dc69d',
                          borderRadius: '4px'
                        }}>
                          <Text variant="bodySm" fontWeight="semibold">Selected: {selectedJacket.title}</Text>
                          <br />
                          <Text variant="bodySm" tone="subdued">{selectedJacket.productType}</Text>
                          <button
                            type="button"
                            onClick={() => handleJacketSelect(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#bf0711',
                              cursor: 'pointer',
                              fontSize: '12px',
                              float: 'right'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                        Search and select a jacket product from your Shopify store
                      </Text>
                    </div>

                    {/* Pants Selection */}
                    <div>
                      <Text variant="bodySm" fontWeight="semibold" as="label" style={{ marginBottom: '8px', display: 'block' }}>
                        Pants
                      </Text>
                      <div style={{ position: 'relative' }}>
                        <TextField
                          value={pantsSearchQuery}
                          onChange={setPantsSearchQuery}
                          placeholder="Search for pants/trousers..."
                          autoComplete="off"
                        />
                        {pantsSearchQuery && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #e1e3e5',
                            borderRadius: '4px',
                            maxHeight: '200px',
                            overflowY: 'auto',
                            zIndex: 1000,
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                          }}>
                            {getFilteredPants().length > 0 ? (
                              getFilteredPants().map(product => (
                                <div
                                  key={product.id}
                                  onClick={() => handlePantsSelect(product)}
                                  style={{
                                    padding: '12px',
                                    cursor: 'pointer',
                                    borderBottom: '1px solid #f1f1f1',
                                    backgroundColor: selectedPants?.id === product.id ? '#f0f9f7' : 'white'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f6f6f7'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = selectedPants?.id === product.id ? '#f0f9f7' : 'white'}
                                >
                                  <div style={{ fontWeight: '500' }}>{product.title}</div>
                                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                                    {product.productType} • ${product.variants?.[0]?.price || 'N/A'}
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div style={{ padding: '12px', color: '#666', textAlign: 'center' }}>
                                No pants/trousers found
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                      {selectedPants && (
                        <div style={{
                          marginTop: '8px',
                          padding: '8px',
                          backgroundColor: '#f0f9f7',
                          border: '1px solid #9dc69d',
                          borderRadius: '4px'
                        }}>
                          <Text variant="bodySm" fontWeight="semibold">Selected: {selectedPants.title}</Text>
                          <br />
                          <Text variant="bodySm" tone="subdued">{selectedPants.productType}</Text>
                          <button
                            type="button"
                            onClick={() => handlePantsSelect(null)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#bf0711',
                              cursor: 'pointer',
                              fontSize: '12px',
                              float: 'right'
                            }}
                          >
                            Remove
                          </button>
                        </div>
                      )}
                      <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                        Search and select pants/trousers from your Shopify store
                      </Text>
                    </div>
                  </FormLayout.Group>
                </div>
              )}

              <TextField
                label="Title"
                value={formData.title}
                onChange={(value) => handleFormChange('title', value)}
                required
                disabled={Array.isArray(editingProduct) && editingProduct.length > 1}
                helpText={Array.isArray(editingProduct) && editingProduct.length > 1 ? "Titles cannot be edited in bulk mode" : undefined}
                style={getFieldStyle('title', editingProduct)}
              />

              <Text variant="headingMd" as="h3">Pricing Stages</Text>
              <Text variant="bodyMd" tone="subdued">
                Configure up to 4 price stages. Each stage applies from a minimum number of days.
              </Text>
              
              {[1, 2, 3, 4].map(stageNum => (
                <Card key={stageNum} sectioned>
                  <BlockStack gap="300">
                    <InlineStack align="space-between">
                      <Text variant="headingSm" as="h4">Price Stage {stageNum}</Text>
                      <Checkbox
                        label="Enable this stage"
                        checked={formData[`priceStage${stageNum}`].enabled}
                        onChange={(checked) => handlePriceStageChange(stageNum, 'enabled', checked)}
                        disabled={false}
                        style={getFieldStyle(`priceStage${stageNum}.enabled`, editingProduct)}
                      />
                    </InlineStack>
                    
                    {formData[`priceStage${stageNum}`].enabled && (
                      <FormLayout.Group>
                        <div style={getFieldStyle(`priceStage${stageNum}.price`, editingProduct)}>
                          <TextField
                            label={`Price per day ($)`}
                            type="number"
                            step="0.01"
                            value={formData[`priceStage${stageNum}`].price}
                            onChange={(value) => handlePriceStageChange(stageNum, 'price', value)}
                            required
                            disabled={false}
                            helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences(`priceStage${stageNum}.price`, editingProduct) ? "Values differ across selected products" : undefined}
                          />
                        </div>
                        <div style={getFieldStyle(`priceStage${stageNum}.minDays`, editingProduct)}>
                          <TextField
                            label="Minimum days"
                            type="number"
                            value={formData[`priceStage${stageNum}`].minDays}
                            onChange={(value) => handlePriceStageChange(stageNum, 'minDays', value)}
                            required
                            disabled={false}
                            helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences(`priceStage${stageNum}.minDays`, editingProduct) ? "Values differ across selected products" : undefined}
                          />
                        </div>
                      </FormLayout.Group>
                    )}
                  </BlockStack>
                </Card>
              ))}

              <div style={getFieldStyle('securityDeposit', editingProduct)}>
                <TextField
                  label="Security Deposit ($)"
                  type="number"
                  step="0.01"
                  value={formData.securityDeposit}
                  onChange={(value) => handleFormChange('securityDeposit', value)}
                  disabled={false}
                  helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('securityDeposit', editingProduct) ? "Values differ across selected products" : undefined}
                />
              </div>

              <FormLayout.Group>
                <div style={getFieldStyle('minimumRentalDays', editingProduct)}>
                  <TextField
                    label="Minimum Rental Days"
                    type="number"
                    value={formData.minimumRentalDays}
                    onChange={(value) => handleFormChange('minimumRentalDays', value)}
                    required
                    disabled={false}
                    helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('minimumRentalDays', editingProduct) ? "Values differ across selected products" : undefined}
                  />
                </div>
                <div style={getFieldStyle('bufferBefore', editingProduct)}>
                  <TextField
                    label="Buffer before (days)"
                    type="number"
                    value={formData.bufferBefore}
                    onChange={(value) => handleFormChange('bufferBefore', value)}
                    helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('bufferBefore', editingProduct) ? "Values differ across selected products" : "Days blocked before rental start (prep / outbound)"}
                    required
                    disabled={false}
                  />
                </div>
                <div style={getFieldStyle('bufferAfter', editingProduct)}>
                  <TextField
                    label="Buffer after (days)"
                    type="number"
                    value={formData.bufferAfter}
                    onChange={(value) => handleFormChange('bufferAfter', value)}
                    helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('bufferAfter', editingProduct) ? "Values differ across selected products" : "Days blocked after rental end (return / cleaning)"}
                    required
                    disabled={false}
                  />
                </div>
              </FormLayout.Group>

              <div style={getFieldStyle('totalQuantity', editingProduct)}>
                <TextField
                  label="Total Quantity"
                  type="number"
                  value={formData.totalQuantity}
                  onChange={(value) => handleFormChange('totalQuantity', value)}
                  required
                  disabled={false}
                  helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('totalQuantity', editingProduct) ? "Values differ across selected products" : undefined}
                />
              </div>

              <FormLayout.Group>
                <div style={getFieldStyle('categories', editingProduct)}>
                  <TextField
                    label="Categories (comma-separated)"
                    value={formData.categories}
                    onChange={(value) => handleFormChange('categories', value)}
                    helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('categories', editingProduct) ? "Values differ across selected products" : "e.g., Electronics, Camera Equipment"}
                    disabled={false}
                  />
                </div>
                <div style={getFieldStyle('tags', editingProduct)}>
                  <TextField
                    label="Tags (comma-separated)"
                    value={formData.tags}
                    onChange={(value) => handleFormChange('tags', value)}
                    helpText={Array.isArray(editingProduct) && editingProduct.length > 1 && getFieldDifferences('tags', editingProduct) ? "Values differ across selected products" : "e.g., professional, outdoor, beginner"}
                    disabled={false}
                  />
                </div>
              </FormLayout.Group>

              {/* Add-On Linking Section */}
              <div style={{ marginTop: '24px', padding: '16px', backgroundColor: '#f6f6f7', borderRadius: '8px' }}>
                <Text variant="headingMd" as="h3">Available Add-Ons</Text>
                <Text variant="bodySm" tone="subdued" as="p" style={{ marginBottom: '16px' }}>
                  Select which add-ons customers can choose when renting this product. Add-ons provide additional items like accessories or upgrades.
                </Text>
                
                {availableAddOns.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '20px' }}>
                    <Text variant="bodyMd" tone="subdued">
                      No add-ons available. Create add-ons first in the Add-Ons section.
                    </Text>
                  </div>
                ) : (
                  <div style={{ display: 'grid', gap: '12px' }}>
                    {availableAddOns.filter(addOn => addOn.isActive).map(addOn => {
                      const isSelected = selectedAddOns.includes(addOn.id);
                      const settings = addOnSettings[addOn.id] || { isRequired: false, displayOrder: 0 };
                      
                      return (
                        <div
                          key={addOn.id}
                          style={{
                            padding: '16px',
                            border: isSelected ? '2px solid #008060' : '1px solid #e1e3e5',
                            borderRadius: '8px',
                            backgroundColor: isSelected ? '#f0f9f7' : 'white',
                            transition: 'all 0.2s ease'
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <Checkbox
                              checked={isSelected}
                              onChange={(checked) => handleAddOnToggle(addOn.id, checked)}
                            />
                            
                            <div style={{ flex: 1 }}>
                              <div style={{ marginBottom: '8px' }}>
                                <Text variant="bodyMd" fontWeight="semibold">
                                  {addOn.name}
                                </Text>
                                {addOn.description && (
                                  <Text variant="bodySm" tone="subdued" style={{ marginTop: '4px' }}>
                                    {addOn.description}
                                  </Text>
                                )}
                              </div>
                              
                              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '8px' }}>
                                {addOn.category && (
                                  <Badge size="small">{addOn.category}</Badge>
                                )}
                                <Badge size="small" status={addOn.allowMultipleSelections ? "info" : "attention"}>
                                  {addOn.allowMultipleSelections ? `Multi-select (${addOn.maxSelections})` : 'Single select'}
                                </Badge>
                                <Badge size="small" tone="subdued">
                                  {addOn.entries?.length || 0} options
                                </Badge>
                              </div>
                              
                              {/* Add-on settings when selected */}
                              {isSelected && (
                                <div style={{ 
                                  marginTop: '12px', 
                                  padding: '12px', 
                                  backgroundColor: '#fafbfb', 
                                  borderRadius: '6px',
                                  border: '1px solid #e1e3e5'
                                }}>
                                  <Text variant="bodySm" fontWeight="semibold" style={{ marginBottom: '8px' }}>
                                    Add-on Settings:
                                  </Text>
                                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                                    <Checkbox
                                      label="Required"
                                      checked={settings.isRequired}
                                      onChange={(checked) => handleAddOnSettingChange(addOn.id, 'isRequired', checked)}
                                      helpText="Customers must select from this add-on"
                                    />
                                    <div style={{ minWidth: '100px' }}>
                                      <TextField
                                        label="Display Order"
                                        type="number"
                                        value={(settings.displayOrder || 0).toString()}
                                        onChange={(value) => handleAddOnSettingChange(addOn.id, 'displayOrder', parseInt(value) || 0)}
                                        helpText="Lower numbers appear first"
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                
                {selectedAddOns.length > 0 && (
                  <div style={{ 
                    marginTop: '16px', 
                    padding: '12px', 
                    backgroundColor: '#e8f5e8', 
                    borderRadius: '6px', 
                    border: '1px solid #9dc69d' 
                  }}>
                    <Text variant="bodyMd" fontWeight="semibold">
                      {selectedAddOns.length} add-on{selectedAddOns.length !== 1 ? 's' : ''} selected
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Customers will see these add-ons when booking this rental product.
                    </Text>
                  </div>
                )}
              </div>
            </FormLayout>
          </Form>
        </Modal.Section>
      </Modal>

      <Modal
        open={conditionModalOpen}
        onClose={() => setConditionModalOpen(false)}
        title={conditionProduct ? `Edit Conditions: ${conditionProduct.title}` : 'Edit Conditions'}
        size="large"
        primaryAction={{
          content: 'Save Conditions',
          onAction: saveConditionRecords,
          loading: conditionsSaving,
          disabled: conditionsLoading || conditionsSaving
        }}
        secondaryActions={[{
          content: 'Cancel',
          onAction: () => setConditionModalOpen(false)
        }]}
      >
        <Modal.Section>
          <BlockStack gap="400">
            {conditionError && (
              <Banner tone="critical" onDismiss={() => setConditionError('')}>
                {conditionError}
              </Banner>
            )}

            {conditionsLoading ? (
              <InlineStack align="center" gap="300">
                <Spinner size="small" />
                <Text variant="bodyMd" tone="subdued">Loading conditions...</Text>
              </InlineStack>
            ) : (
              <>
                <InlineStack align="space-between" blockAlign="center">
                  <BlockStack gap="050">
                    <Text variant="bodyMd" fontWeight="semibold">
                      Tracked items: {conditionRecords.length} of {conditionProduct?.totalQuantity || 0}
                    </Text>
                    <Text variant="bodySm" tone="subdued">
                      Each record represents one physical item and has its own permanent ID.
                    </Text>
                  </BlockStack>
                  <Button
                    onClick={addConditionRecord}
                    disabled={conditionRecords.length >= (conditionProduct?.totalQuantity || 0)}
                  >
                    Add Item Condition
                  </Button>
                </InlineStack>

                <Text variant="bodyMd" tone="subdued">
                  {Math.max(0, (conditionProduct?.totalQuantity || 0) - conditionRecords.filter((item) => item.status === 'unavailable').length)} rentable · {conditionRecords.filter((item) => item.status === 'unavailable').length} unavailable. Unavailable items are excluded from rental availability.
                </Text>
                {conditionRecords.length === 0 ? (
                  <Card>
                    <Text variant="bodyMd" tone="subdued">No item conditions tracked yet.</Text>
                  </Card>
                ) : (
                  <BlockStack gap="300">
                    {conditionRecords.map((record, index) => (
                      <Card key={record.id || record.clientId || index}>
                        <BlockStack gap="300">
                          <InlineStack align="space-between" blockAlign="center">
                            <Text variant="headingSm">
                              Physical item {index + 1}
                            </Text>
                            <Button
                              variant="plain"
                              tone="critical"
                              onClick={() => setConditionRecords((records) => records.filter((_, itemIndex) => itemIndex !== index))}
                            >
                              Remove
                            </Button>
                          </InlineStack>
                          <TextField
                            label="Item ID (API)"
                            value={record.id != null ? String(record.id) : 'Assigned after saving'}
                            readOnly
                            autoComplete="off"
                            helpText="Permanent unique ID for retrieving, updating, or deleting this physical item through the API."
                          />
                          <Select
                            label="Availability status"
                            options={[
                              { label: 'Available', value: 'available' },
                              { label: 'Unavailable', value: 'unavailable' },
                            ]}
                            value={record.status || 'available'}
                            onChange={(value) => updateConditionRecord(index, 'status', value)}
                            helpText="Mark unavailable during repairs or when this item cannot be rented."
                          />
                          <Select
                            label="Condition"
                            options={[
                              { label: 'New', value: 'new' },
                              { label: 'Very good', value: 'very_good' },
                              { label: 'Good', value: 'good' },
                              { label: 'Fair', value: 'fair' },
                            ]}
                            value={record.condition}
                            onChange={(value) => updateConditionRecord(index, 'condition', value)}
                          />
                          <TextField
                            label="Comment"
                            value={record.comment || ''}
                            onChange={(value) => updateConditionRecord(index, 'comment', value)}
                            multiline={2}
                            maxLength={2000}
                            showCharacterCount
                            autoComplete="off"
                          />
                        </BlockStack>
                      </Card>
                    ))}
                  </BlockStack>
                )}
              </>
            )}
          </BlockStack>
        </Modal.Section>
      </Modal>


      {/* Rental Booking Modal */}
      <RentalModal
        isOpen={rentalModalActive}
        onClose={() => setRentalModalActive(false)}
        preselectedProduct={selectedProduct}
        onSuccess={handleRentalSuccess}
      />
    </div>
  );
};

export default Products;
